import React, { useState, useRef } from 'react';
import { uploadAttachment, deleteAttachment } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Upload,
  FileText,
  Image,
  Video,
  Trash2,
  Download,
  Loader2,
  File,
  FileSpreadsheet,
  FileImage,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AttachmentsSection = ({ workOrderId, attachments = [], onUpdate }) => {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const getFileIcon = (fileType, mimeType) => {
    if (fileType === 'image') return <Image className="h-5 w-5 text-green-600" />;
    if (fileType === 'video') return <Video className="h-5 w-5 text-purple-600" />;
    if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) {
      return <FileSpreadsheet className="h-5 w-5 text-emerald-600" />;
    }
    if (mimeType?.includes('pdf')) return <FileText className="h-5 w-5 text-red-600" />;
    if (mimeType?.includes('word') || mimeType?.includes('document')) {
      return <FileText className="h-5 w-5 text-blue-600" />;
    }
    if (mimeType?.includes('dwg') || mimeType?.includes('dxf') || mimeType?.includes('autocad')) {
      return <FileImage className="h-5 w-5 text-orange-600" />;
    }
    return <File className="h-5 w-5 text-gray-600" />;
  };

  const getFileTypeLabel = (fileType, mimeType, filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (fileType === 'image') return 'Image';
    if (fileType === 'video') return 'Video';
    if (ext === 'pdf') return 'PDF';
    if (ext === 'doc' || ext === 'docx') return 'Word';
    if (ext === 'xls' || ext === 'xlsx') return 'Excel';
    if (ext === 'dwg') return 'AutoCAD DWG';
    if (ext === 'dxf') return 'AutoCAD DXF';
    return 'Document';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        toast.error(t('fileTooLarge'));
        return;
      }
      setSelectedFile(file);
      setShowUploadDialog(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      await uploadAttachment(workOrderId, selectedFile, description);
      toast.success(t('fileUploaded'));
      setShowUploadDialog(false);
      setSelectedFile(null);
      setDescription('');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!window.confirm(t('confirmDeleteAttachment'))) return;

    setDeletingId(attachmentId);
    try {
      await deleteAttachment(workOrderId, attachmentId);
      toast.success(t('attachmentDeleted'));
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('deleteFailed'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = (attachment) => {
    const url = `${BACKEND_URL}${attachment.file_path}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.original_filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const canPreview = (attachment) => {
    return attachment.file_type === 'image' || 
           attachment.file_type === 'video' ||
           attachment.mime_type === 'application/pdf';
  };

  return (
    <Card data-testid="attachments-section">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading flex items-center gap-2">
          <FileText className="h-5 w-5" />
          {t('attachments')} ({attachments.length})
        </CardTitle>
        <div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi,.webm,.wmv"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-attachment-button"
          >
            <Upload className="mr-2 h-4 w-4" />
            {t('uploadFile')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {attachments.length > 0 ? (
          <div className="space-y-3">
            {attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center justify-between p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors"
                data-testid={`attachment-${attachment.id}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {getFileIcon(attachment.file_type, attachment.mime_type)}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{attachment.original_filename}</p>
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mt-1">
                      <Badge variant="outline" className="text-xs">
                        {getFileTypeLabel(attachment.file_type, attachment.mime_type, attachment.original_filename)}
                      </Badge>
                      <span>{formatFileSize(attachment.file_size)}</span>
                      <span>•</span>
                      <span>{attachment.uploaded_by_name}</span>
                      <span>•</span>
                      <span>{format(new Date(attachment.uploaded_at), 'MMM d, yyyy')}</span>
                    </div>
                    {attachment.description && (
                      <p className="text-xs text-muted-foreground mt-1 italic">
                        {attachment.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {canPreview(attachment) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setPreviewAttachment(attachment)}
                      title={t('preview')}
                    >
                      <Image className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDownload(attachment)}
                    title={t('download')}
                    data-testid={`download-${attachment.id}`}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(attachment.id)}
                    disabled={deletingId === attachment.id}
                    className="text-destructive hover:text-destructive"
                    title={t('delete')}
                    data-testid={`delete-${attachment.id}`}
                  >
                    {deletingId === attachment.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>{t('noAttachments')}</p>
            <p className="text-xs mt-1">{t('supportedFormats')}</p>
          </div>
        )}
      </CardContent>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('uploadFile')}</DialogTitle>
            <DialogDescription>
              {t('uploadFileDesc')}
            </DialogDescription>
          </DialogHeader>
          {selectedFile && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                {getFileIcon(
                  selectedFile.type.startsWith('image/') ? 'image' : 
                  selectedFile.type.startsWith('video/') ? 'video' : 'document',
                  selectedFile.type
                )}
                <div>
                  <p className="font-medium">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(selectedFile.size)}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">{t('descriptionOptional')}</Label>
                <Input
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('fileDescriptionPlaceholder')}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('uploading')}
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  {t('upload')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewAttachment} onOpenChange={() => setPreviewAttachment(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{previewAttachment?.original_filename}</DialogTitle>
          </DialogHeader>
          {previewAttachment && (
            <div className="flex items-center justify-center overflow-auto">
              {previewAttachment.file_type === 'image' && (
                <img
                  src={`${BACKEND_URL}${previewAttachment.file_path}`}
                  alt={previewAttachment.original_filename}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              )}
              {previewAttachment.file_type === 'video' && (
                <video
                  src={`${BACKEND_URL}${previewAttachment.file_path}`}
                  controls
                  className="max-w-full max-h-[70vh]"
                >
                  Your browser does not support the video tag.
                </video>
              )}
              {previewAttachment.mime_type === 'application/pdf' && (
                <iframe
                  src={`${BACKEND_URL}${previewAttachment.file_path}`}
                  className="w-full h-[70vh]"
                  title={previewAttachment.original_filename}
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AttachmentsSection;
